import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { enhancedSupabase } from "@/integrations/supabase/enhanced-client";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Users, Eye, ThumbsUp, UserPlus, Calendar, MapPin, FolderOpen, BarChart2, PieChart, Image, Trophy } from "lucide-react";
import SuperadminLayout from "@/components/superadmin/SuperadminLayout";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Legend,
  LineChart,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from 'recharts';
import { Checkbox } from '@/components/ui/checkbox';
import { DateRangeDropdownV2 } from "@/components/ui/DateRangeDropdownV2";
import { DateRangeSelector } from "@/components/ui/DateRangeSelector";

const MetricCard = ({
  title,
  value,
  icon,
  description,
}: {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  description?: string;
}) => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-sm font-medium">{title}</CardTitle>
      {icon}
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold">{value}</div>
      {description && (
        <p className="text-xs text-muted-foreground mt-1">{description}</p>
      )}
    </CardContent>
  </Card>
);

// Define the platform metrics interface
interface PlatformMetrics {
  dau: number;
  wau: number;
  new_signups_week: number;
  total_users: number;
  total_views: number;
  total_likes: number;
  top_locations: { location: string; amount: number }[];
  top_profiles: {
    display_name: string;
    username: string;
    view_count: number;
    id: string;
  }[];
  daily_signup: { date: string; amount: number }[];
}

// Add the new portfolio analytics interface
interface PortfolioAnalytics {
  total_users: number;
  users_with_portfolios: number;
  total_projects: number;
  avg_projects_per_portfolio: number;
  portfolio_distribution: {
    one_project: number;
    two_projects: number;
    three_plus_projects: number;
  };
  portfolio_completion_rate: number;
  portfolio_engagement: {
    total_portfolio_views: number;
    total_portfolio_likes: number;
    avg_portfolio_views: number;
    avg_portfolio_likes: number;
  };
  portfolio_images: {
    total_images: number;
    avg_images_per_project: number;
    avg_images_per_portfolio: number;
  };
  top_portfolios: Array<{
    profile_id: string;
    display_name: string;
    username: string;
    view_count: number;
    like_count: number;
    project_count: number;
  }>;
  portfolio_trends: Array<{
    date: string;
    new_portfolios: number;
    new_projects: number;
    new_images: number;
  }>;
}

// --- Analytics Dashboard Chart Component ---
type AnalyticsChartData = {
  date: string;
  signups: number;
  projects: number;
  images: number;
};

const chartLines = [
  { key: 'signups', label: 'New Sign Ups', color: '#2563eb' },
  { key: 'projects', label: 'New Projects', color: '#22c55e' },
  { key: 'images', label: 'Images Added', color: '#f59e42' },
];

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload) return null;
  return (
    <div className="bg-card border border-border rounded-lg p-3 shadow-lg min-w-[170px]">
      <div className="font-semibold text-black text-base mb-3">Date: {label && new Date(label).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' })}</div>
     
      <div className="text-sm mb-1 text-blue-600">
        New Sign Ups: <span className="font-bold">{payload.find((e: any) => e.dataKey === 'signups')?.value ?? 0}</span>
      </div>
      <div className="text-sm mb-1 text-green-600">
        New Projects: <span className="font-bold">{payload.find((e: any) => e.dataKey === 'projects')?.value ?? 0}</span>
      </div>
      <div className="text-sm text-orange-500">
        Images Added: <span className="font-bold">{payload.find((e: any) => e.dataKey === 'images')?.value ?? 0}</span>
      </div>
    </div>
  );
}

function AnalyticsDashboardChart({ data }: { data: AnalyticsChartData[] }) {
  const [visible, setVisible] = React.useState({
    signups: true,
    projects: true,
    images: true,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">Analytics Dashboard</CardTitle>
        <CardDescription>
          Track signups, projects, and images
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-6 mb-2">
          {chartLines.map((line) => (
            <label key={line.key} className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={visible[line.key as keyof typeof visible]}
                onCheckedChange={() =>
                  setVisible((v) => ({ ...v, [line.key]: !v[line.key as keyof typeof visible] }))
                }
                className="border-muted-foreground"
              />
              <span
                className="text-xs font-medium flex items-center gap-1"
                style={{ color: line.color }}
              >
                <span
                  className="inline-block w-3 h-3 rounded-full"
                  style={{ background: line.color }}
                />
                {line.label}
              </span>
            </label>
          ))}
        </div>

        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12 }}
              tickFormatter={(value) =>
                new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' })
              }
              axisLine={false}
              tickLine={false}
            />
            <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />

            <Tooltip content={<CustomTooltip />} />
           
            {chartLines.map(
              (line) =>
                visible[line.key as keyof typeof visible] && (
                  <Line
                    key={line.key}
                    type="monotone"
                    dataKey={line.key}
                    name={line.label}
                    stroke={line.color}
                    strokeWidth={3}
                    dot={{
                      r: 4,
                      stroke: "#fff",
                      strokeWidth: 2,
                      fill: line.color,
                    }}
                    activeDot={{
                      r: 6,
                      stroke: "#fff",
                      strokeWidth: 2,
                      fill: line.color,
                    }}
                  />
                )
            )}
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

// --- Mock or real data for the chart ---
function getAnalyticsChartData(metrics: any, portfolioTrends: any[]): AnalyticsChartData[] {
  if (metrics?.daily_signup && portfolioTrends) {
    const trendsByDate: Record<string, any> = Object.fromEntries(
      portfolioTrends.map((d: any) => [d.date, d])
    );
    return metrics.daily_signup.map((signup: any) => {
      const trend = trendsByDate[signup.date] || {};
      return {
        date: signup.date,
        signups: signup.amount,
        projects: trend.new_projects || 0,
        images: trend.new_images || 0,
      };
    });
  }
  // Fallback: mock data
  const today = new Date();
  return Array.from({ length: 30 }, (_, i) => {
    const date = new Date(today);
    date.setDate(today.getDate() - (29 - i));
    return {
      date: date.toISOString().split('T')[0],
      signups: Math.floor(Math.random() * 10) + 1,
      projects: Math.floor(Math.random() * 8) + 1,
      images: Math.floor(Math.random() * 15) + 1,
    };
  });
}

const getLast30DaysRange = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - 29);
  return { from, to };
};

const SuperadminDashboard = () => {
  const [timeRange] = useState<"week" | "month">("week");
  // Date range state for analytics filter (matches new DateRange type)
  const [dateRange, setDateRange] = useState<{ from: Date | undefined; to?: Date | undefined }>(getLast30DaysRange());

  // Fetch key metrics using the RPC function
  const { data: metrics, isLoading: metricsLoading } = useQuery({
    queryKey: ["superadmin-metrics", dateRange],
    queryFn: async () => {
      // Use the get_platform_metrics RPC function, pass date range if supported
      // If not supported, filter client-side as fallback
      const { data, error } =
        await enhancedSupabase.rpc<"get_profile_statistics">(
          "get_profile_statistics",
          dateRange.from && dateRange.to ? {
            from: dateRange.from.toISOString().split('T')[0],
            to: dateRange.to.toISOString().split('T')[0],
          } : {}
        );
      if (error) throw new Error(error.message);
      return data as PlatformMetrics;
    },
    enabled: !!dateRange.from && !!dateRange.to,
  });

  // Fetch portfolio trends using the new function
  const { data: portfolioTrends, isLoading: trendsLoading } = useQuery({
    queryKey: ["portfolio-trends", dateRange],
    queryFn: async () => {
      // If backend supports, pass date range; else filter client-side
      const { data, error } = await supabase.rpc('get_portfolio_trends',
        dateRange.from && dateRange.to ? {
          from: dateRange.from.toISOString().split('T')[0],
          to: dateRange.to.toISOString().split('T')[0],
        } : {}
      );
      if (error) throw new Error(error.message);
      return (data ?? []) as any[];
    },
    enabled: !!dateRange.from && !!dateRange.to,
  });

  // Debug: log metrics and merged chart data
  React.useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('metrics:', metrics);
    // eslint-disable-next-line no-console
    console.log('portfolioTrends:', portfolioTrends);
    // eslint-disable-next-line no-console
    console.log('analytics chart data:', getAnalyticsChartData(metrics, portfolioTrends));
  }, [metrics, portfolioTrends]);

  // Remove unused signupData (mock)

  // Add the new portfolio analytics query
  const { data: portfolioAnalytics, isLoading: portfolioLoading } = useQuery({
    queryKey: ["portfolio-analytics", dateRange],
    queryFn: async () => {
      const { data, error } = await enhancedSupabase.rpc<"get_portfolio_analytics">(
        "get_portfolio_analytics",
        dateRange.from && dateRange.to ? {
          from: dateRange.from.toISOString().split('T')[0],
          to: dateRange.to.toISOString().split('T')[0],
        } : {}
      );
      if (error) throw new Error(error.message);
      return data as PortfolioAnalytics;
    },
    enabled: !!dateRange.from && !!dateRange.to,
  });

  // --- Normalize and aggregate top_locations for display ---
  const normalizedTopLocations = React.useMemo(() => {
    if (!metrics?.top_locations) return [];
    const map = new Map();
    metrics.top_locations.forEach(({ location, amount }) => {
      // Title Case normalization (handles all-lower, all-upper, etc.)
      const norm = location
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      if (map.has(norm)) {
        map.set(norm, map.get(norm) + amount);
      } else {
        map.set(norm, amount);
      }
    });
    // Convert back to array of { location, amount }
    return Array.from(map.entries()).map(([location, amount]) => ({ location, amount }));
  }, [metrics?.top_locations]);

  // Filter chart data by date range
  const filteredChartData = React.useMemo(() => {
    const allData = getAnalyticsChartData(metrics, portfolioTrends ?? []);
    if (dateRange.from && dateRange.to) {
      const fromTime = dateRange.from.setHours(0,0,0,0);
      const toTime = dateRange.to.setHours(23,59,59,999);
      return allData.filter((d) => {
        const t = new Date(d.date).getTime();
        return t >= fromTime && t <= toTime;
      });
    }
    return allData;
  }, [metrics, portfolioTrends, dateRange]);

  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-white rounded-lg p-5 shadow-sm border border-gray-200">
          <div className="space-y-8">
            {/* Dashboard content starts here */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold tracking-tight">Dashboard</h2>
                <p className="text-muted-foreground">
                  Platform overview and key metrics
                </p>
              </div>
              <div className="flex-shrink-0">
                <DateRangeDropdownV2 value={dateRange} onChange={setDateRange} />
              </div>
            </div>

            {/* Key Metrics */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <MetricCard
                title="Total Users"
                value={metricsLoading ? "Loading..." : metrics?.total_users || 0}
                icon={<Users className="h-4 w-4 text-muted-foreground" />}
              />
              <MetricCard
                title="Total Profile Views"
                value={metricsLoading ? "Loading..." : metrics?.total_views || 0}
                icon={<Eye className="h-4 w-4 text-muted-foreground" />}
              />
              <MetricCard
                title="Total Profile Likes"
                value={metricsLoading ? "Loading..." : metrics?.total_likes || 0}
                icon={<ThumbsUp className="h-4 w-4 text-muted-foreground" />}
              />
            </div>

            

            {/* --- Analytics Dashboard Chart at the top --- */}
            <AnalyticsDashboardChart data={filteredChartData} />
            {/* Portfolio Analytics Section */}
            <div className="mt-8 space-y-8">
              <h3 className="text-xl font-semibold mb-4">Portfolio Analytics</h3>
              {/* Key Metrics Grid */}
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <MetricCard
                  title="Users with Portfolios"
                  value={portfolioLoading ? "Loading..." : `${portfolioAnalytics?.users_with_portfolios || 0} out of ${portfolioAnalytics?.total_users || 0}`}
                  icon={<FolderOpen className="h-4 w-4 text-muted-foreground" />}
                  description={`${portfolioAnalytics?.portfolio_completion_rate || 0}% adoption rate`}
                />
                <MetricCard
                  title="Total Portfolio Projects"
                  value={portfolioLoading ? "Loading..." : portfolioAnalytics?.total_projects || 0}
                  icon={<BarChart2 className="h-4 w-4 text-muted-foreground" />}
                />
                <MetricCard
                  title="Avg Projects per Portfolio"
                  value={portfolioLoading ? "Loading..." : portfolioAnalytics?.avg_projects_per_portfolio || 0}
                  icon={<PieChart className="h-4 w-4 text-muted-foreground" />}
                />
                <MetricCard
                  title="Total Portfolio Images"
                  value={portfolioLoading ? "Loading..." : portfolioAnalytics?.portfolio_images?.total_images || 0}
                  icon={<Image className="h-4 w-4 text-muted-foreground" />}
                  description={`${portfolioAnalytics?.portfolio_images?.avg_images_per_project || 0} avg per project`}
                />
              </div>

              {/* Portfolio Distribution Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <PieChart className="h-4 w-4" />
                    <span>Portfolio Distribution</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPieChart>
                        <Pie
                          data={[
                            { name: "1 Project", value: portfolioAnalytics?.portfolio_distribution.one_project || 0 },
                            { name: "2 Projects", value: portfolioAnalytics?.portfolio_distribution.two_projects || 0 },
                            { name: "3+ Projects", value: portfolioAnalytics?.portfolio_distribution.three_plus_projects || 0 },
                          ]}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          <Cell fill="#2563eb" />
                          <Cell fill="#3b82f6" />
                          <Cell fill="#60a5fa" />
                        </Pie>
                        <Tooltip />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Top Performing Portfolios */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <Trophy className="h-4 w-4" />
                    <span>Top Performing Portfolios</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {portfolioAnalytics?.top_portfolios.map((portfolio) => (
                      <div
                        key={portfolio.profile_id}
                        className="flex items-center justify-between"
                      >
                        <div className="font-medium">
                          {portfolio.display_name || portfolio.username}
                        </div>
                        <div className="flex items-center gap-4 text-muted-foreground">
                          <div className="flex items-center">
                            <Eye className="mr-1 h-3 w-3" />
                            <span>{portfolio.view_count}</span>
                          </div>
                          <div className="flex items-center">
                            <ThumbsUp className="mr-1 h-3 w-3" />
                            <span>{portfolio.like_count}</span>
                          </div>
                          <div className="flex items-center">
                            <FolderOpen className="mr-1 h-3 w-3" />
                            <span>{portfolio.project_count} projects</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

            {/* Top Locations Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <MapPin className="h-4 w-4" />
                  <span>Top Locations</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {normalizedTopLocations.length === 0 && (
                    <div className="text-muted-foreground">No data</div>
                  )}
                  {normalizedTopLocations.map((loc) => (
                    <div key={loc.location} className="flex justify-between">
                      <span>{loc.location}</span>
                      <span className="text-muted-foreground">{loc.amount} users</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperadminDashboard;
